import * as angular from 'angular';
import * as d3 from 'd3';

import { GlobalWorkerOptions, getDocument, SVGGraphics } from 'pdfjs-dist/legacy/build/pdf.min';
import { PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api';
import * as PDFJSWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry';

interface PDFJSRenderTask {
    promise: Promise<void>;
    cancel(): void;
}

// ES5 build is required from chromium, so we need to configure worker
GlobalWorkerOptions.workerSrc = PDFJSWorker;

export interface IPdfToSvgService {
    convert(pdfFileName: string): angular.IPromise<SVGSVGElement>;
    convertToBase64(pdfFileName: string, isRead?: boolean): angular.IPromise<string>;
    convertBlobToBase64(base64Data: any): angular.IPromise<string>;
}

export class PdfToSvgService implements IPdfToSvgService {
    static serviceId = 'pdfToSvgService';

    private _renderTask: PDFJSRenderTask;

    static $inject = ['$q'];

    constructor(private _$q: angular.IQService) {
    }

    convert(pdfFileName: string): angular.IPromise<SVGSVGElement> {
        const defered = this._$q.defer<SVGSVGElement>();
        let elt: d3.Selection<any>;

        getDocument(pdfFileName)
            .promise
            .then(pdf => pdf.getPage(1))
            .then(page => {
                const scale = 1;
                const viewport = page.getViewport({ scale: scale });
                const fantomRenderer = 'fantom-renderer';
                elt = d3.select('body')
                    .append('svg')
                    .attr('id', fantomRenderer);
                const container = document.getElementById(fantomRenderer);
                container.style.width = viewport.width.toString();
                container.style.height = viewport.height.toString();
                return page.getOperatorList()
                    .then(opList => {
                        const svgGfx = new SVGGraphics(page.commonObjs, page.objs);
                        return svgGfx.getSVG(opList, viewport);
                    })
                    .then(svg => {
                        elt.remove();
                        defered.resolve(svg);
                    })
                    .catch(reason => defered.reject(reason));
            })
            .catch((reason) => defered.reject(reason));
        return defered.promise;
    }

    convertToBase64(pdfFileName: string, isRead: boolean = false): angular.IPromise<string> {
        const arg = isRead
            ? { data: pdfFileName }
            : pdfFileName;
        const promise = getDocument(arg).promise;
        return this.PdfToBase64(promise);
    }

    convertBlobToBase64(blob: any): angular.IPromise<string> {
        const promise = getDocument({ data: blob }).promise;
        return this.PdfToBase64(promise);
    }

    private PdfToBase64(promise: Promise<PDFDocumentProxy>): angular.IPromise<string> {
        const defered = this._$q.defer<string>();
        let elt: d3.Selection<any>;
        promise
            .then(pdf => pdf.getPage(1))
            .then(page => {
                const scale = 4;
                const viewport = page.getViewport({ scale });

                // Prepare canvas using PDF page dimensions
                elt = d3.select('body')
                    .append('canvas')
                    .style('display', 'none')
                    .attr('id', 'the-canvas');
                const canvas = document.getElementById('the-canvas') as HTMLCanvasElement;
                const context = canvas.getContext('2d');
                canvas.height = viewport.height;
                canvas.width = viewport.width;

                // Render PDF page into canvas context
                const renderContext = {
                    canvasContext: context,
                    viewport: viewport
                };
                if (this._renderTask) {
                    this._renderTask.cancel();
                    this._renderTask = null;
                }

                this._renderTask = page.render(renderContext);
                this._renderTask.promise
                    .then(() => {
                        const dataUrl = canvas.toDataURL();
                        elt.remove();
                        defered.resolve(dataUrl);
                        this._renderTask = null;
                    })
                    .catch(reason => {
                        this._renderTask = null;
                        console.error('PDF Rendering failed.');
                        console.error(reason);
                    });
            })
            .catch(reason => defered.reject(reason));

        return defered.promise;
    }
}